<template>
  <v-dialog
    :value="value"
    max-width="560px"
    persistent
    scrollable
    @input="$emit('input', $event)"
  >
    <v-card>
      <v-card-title class="font-weight-medium secondary--text primary py-3">
        <v-icon class="mr-2" color="secondary" small>mdi-check-circle-outline</v-icon>
        <span>{{ title }}</span>
        <v-spacer></v-spacer>
        <v-btn icon x-small :disabled="submitting" @click="close">
          <v-icon x-small color="secondary">mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <!-- Breadcrumb Navigation -->
      <div class="rst-breadcrumb pa-3">
        <span
          v-for="(label, idx) in stepLabels"
          :key="label"
          class="rst-breadcrumb__group"
        >
          <span
            :class="['rst-breadcrumb__item', { 'rst-breadcrumb__item--active': step === idx + 1 }]"
          >
            {{ label }}
          </span>
          <span v-if="idx < stepLabels.length - 1" class="rst-breadcrumb__sep">/</span>
        </span>
      </div>

      <v-divider></v-divider>

      <v-card-text class="rst-content">
        <TransactionDetailsForm
          v-show="step === 1"
          ref="detailsForm"
          v-model="form"
          :readonly="readonly"
        />

        <TransactionReview
          v-if="step === 2"
          :context="context"
          :transaction="form"
        />

        <TransactionConfirm v-if="step === 3" :context="context" />
      </v-card-text>

      <v-divider></v-divider>
      <v-card-actions class="pa-3">
        <!-- Already finalized: the details are shown for reference only. -->
        <template v-if="readonly">
          <v-btn v-if="step === 2" text small @click="step = 1">
            <v-icon small left>mdi-arrow-left</v-icon>
            Back
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn v-if="step === 1" text small color="primary" @click="step = 2">
            Review
            <v-icon small right>mdi-arrow-right</v-icon>
          </v-btn>
          <v-btn text small @click="close">Close</v-btn>
        </template>

        <template v-else-if="step === 1">
          <v-btn text small :disabled="submitting" @click="close">Cancel</v-btn>
          <v-spacer></v-spacer>
          <v-btn text small color="primary" @click="goNext">
            Next
            <v-icon small right>mdi-arrow-right</v-icon>
          </v-btn>
        </template>

        <template v-else-if="step === 2">
          <v-btn text small @click="step = 1">
            <v-icon small left>mdi-arrow-left</v-icon>
            Back
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn text small color="primary" @click="step = 3">
            Next
            <v-icon small right>mdi-arrow-right</v-icon>
          </v-btn>
        </template>

        <template v-else>
          <v-btn text small :disabled="submitting" @click="step = 2">
            <v-icon small left>mdi-arrow-left</v-icon>
            Back
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn depressed small color="success" :loading="submitting" @click="submit">
            <v-icon small left>mdi-check</v-icon>
            {{ context.historyId ? "Update" : "Finalize" }}
          </v-btn>
        </template>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import TransactionConfirm from "./TransactionConfirm.vue";
import TransactionDetailsForm from "./TransactionDetailsForm.vue";
import TransactionReview from "./TransactionReview.vue";
import { emptyTransaction } from "./transaction";

export default {
  name: "RevenueSplitTransactionDialog",

  components: { TransactionConfirm, TransactionDetailsForm, TransactionReview },

  props: {
    value: { type: Boolean, default: false },
    /**
     * {
     *   historyId,            // null when the period has never been finalized
     *   partnerId, partnerName,
     *   month, year, fortnight,
     *   periodLabel,
     *   totalRevenue,
     *   totalPartnerShare,    // null hides the row
     *   transaction           // existing values to prefill, optional
     * }
     */
    context: { type: Object, required: true },
    /** View-only: the period already has transaction details, so nothing is saved. */
    readonly: { type: Boolean, default: false },
  },

  data() {
    return {
      step: 1,
      submitting: false,
      form: emptyTransaction(),
    };
  },

  computed: {
    title() {
      if (this.readonly) return "Transaction Details";
      return this.context.historyId ? "Update Transaction Details" : "Finalize Split";
    },

    stepLabels() {
      return this.readonly ? ["Details", "Review"] : ["Details", "Review", "Confirm"];
    },
  },

  watch: {
    // Reopening for a different period must not carry the previous one's values.
    value(opened) {
      if (!opened) return;
      this.step = 1;
      this.form = { ...emptyTransaction(), ...(this.context.transaction || {}) };
      this.$nextTick(() => this.$refs.detailsForm?.reset());
    },
  },

  methods: {
    close() {
      this.$emit("input", false);
    },

    goNext() {
      if (!this.$refs.detailsForm.validate()) {
        this.$store.commit("showSnackbar", {
          message: "Transaction details are required to proceed",
          color: "error",
        });
        return;
      }
      this.step = 2;
    },

    async submit() {
      this.submitting = true;
      try {
        const transaction_details = { ...this.form };

        if (this.context.historyId) {
          await this.$axios.put(
            `/revenue_split_histories/${this.context.historyId}`,
            { transaction_details }
          );
        } else {
          await this.$axios.post("/revenue_split_finalize", {
            partner_id: this.context.partnerId,
            month: this.context.month,
            year: this.context.year,
            fortnight: this.context.fortnight || "",
            total_amount: this.context.totalRevenue,
            transaction_details,
          });
        }

        this.$store.commit("showSnackbar", {
          message: this.context.historyId
            ? "Transaction details updated"
            : "Revenue split finalized successfully!",
          color: "success",
        });

        this.close();
        this.$emit("saved");
      } catch (err) {
        this.$store.commit("showSnackbar", {
          message:
            err?.response?.data?.message ||
            err?.response?.data?.error ||
            "Failed to save transaction details",
          color: "error",
        });
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style scoped>
.rst-breadcrumb {
  display: flex;
  align-items: center;
  font-size: 12px;
}

.rst-breadcrumb__group {
  display: inline-flex;
  align-items: center;
}

.rst-breadcrumb__item {
  color: #98a2b3;
}

.rst-breadcrumb__item--active {
  color: var(--v-primary-base, #00aff1);
  font-weight: 600;
}

.rst-breadcrumb__sep {
  margin: 0 6px;
  color: #d0d5dd;
}

.rst-content {
  padding-top: 16px !important;
  min-height: 190px;
}
</style>
